import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TOPICS, TOPIC_LIST } from "@/lib/trainingContent";
import {
  Upload, MapPin, CheckCircle2, ScanLine, PackageCheck, FileSpreadsheet, BarChart3,
  Shuffle, Printer, ArrowRight, Play, ClipboardList, Boxes, Users, AlertTriangle,
  Clock, BookOpen, Film, Truck, ShieldAlert, RotateCcw, FilePlus, ClipboardCheck,
  ArrowRightCircle, DollarSign, Eye, SearchCheck, ListChecks, Save,
} from "lucide-react";

const ICONS = {
  Upload, MapPin, CheckCircle2, ScanLine, PackageCheck, FileSpreadsheet, BarChart3,
  Shuffle, Boxes, Users, Truck, ShieldAlert, RotateCcw, FilePlus, ClipboardCheck,
  ArrowRightCircle, DollarSign, Eye, SearchCheck, ListChecks, Save,
};
const TONES = { blue: "bg-bh-blue text-white", orange: "bg-bh-orange text-bh-navy-deep", emerald: "bg-emerald-600 text-white" };
const TABS = [
  { v: "guide", l: "Step-by-step guide", icon: BookOpen },
  { v: "script", l: "Video script", icon: Film },
  { v: "sop", l: "Print SOP", icon: ClipboardList },
];

function Icon({ name, className }) {
  const C = ICONS[name] || Boxes;
  return <C className={className} />;
}

export default function Training() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const topicKey = params.get("topic");
  const topic = TOPICS[topicKey];
  const [tab, setTab] = useState("guide");

  // ---- Hub (topic picker) ----
  if (!topic) {
    return (
      <div className="space-y-6" data-testid="training-hub">
        <div>
          <span className="inline-block text-xs uppercase tracking-wider font-semibold text-bh-orange bg-orange-100 px-2 py-0.5 rounded">Training</span>
          <h1 className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Manrope" }}>Team how-to guides</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Beginner-friendly walkthroughs for every workflow. Each guide includes step-by-step instructions, a video recording script, and a printable one-page SOP.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOPIC_LIST.map((k) => {
            const t = TOPICS[k];
            return (
              <button key={k} onClick={() => { setTab("guide"); setParams({ topic: k }); }} data-testid={`training-topic-${k}`} className="text-left bg-white border border-slate-200 hover:border-bh-blue rounded-2xl p-5 transition-colors group">
                <div className="w-12 h-12 rounded-2xl bg-bh-blue text-white flex items-center justify-center mb-3"><Icon name={t.icon} className="w-6 h-6" /></div>
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{t.title}</h3>
                <p className="text-sm text-slate-500 mt-1">{t.subtitle}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bh-blue">Open guide <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ---- Topic detail ----
  return (
    <div className="space-y-6" data-testid={`training-topic-page-${topic.key}`}>
      <style>{`@media print { body * { visibility: hidden !important; } #sop-print, #sop-print * { visibility: visible !important; } #sop-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; } .no-print { display: none !important; } }`}</style>

      <div className="flex flex-wrap items-start justify-between gap-3 no-print">
        <div>
          <button onClick={() => setParams({})} data-testid="training-back" className="text-sm font-semibold text-bh-blue hover:text-bh-blue-dark inline-flex items-center gap-1 mb-2"><ArrowRight className="w-4 h-4 rotate-180" /> All guides</button>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{topic.title}</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">{topic.subtitle}</p>
        </div>
        <button onClick={() => window.print()} data-testid="training-print-btn" className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-semibold px-4 py-2.5 rounded-md shrink-0"><Printer className="w-4 h-4" /> Print one-page SOP</button>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200 no-print overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`training-tab-${t.v}`} className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === t.v ? "border-bh-blue text-bh-blue" : "border-transparent text-slate-500 hover:text-slate-800"}`}><t.icon className="w-4 h-4" /> {t.l}</button>
        ))}
      </div>

      {tab === "guide" && (
        <div className="space-y-4 no-print" data-testid="training-guide">
          {topic.steps.map((s) => (
            <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-4" data-testid={`training-step-${s.n}`}>
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${TONES[s.tone]}`}><Icon name={s.icon} className="w-6 h-6" /></div>
              <div className="min-w-0 flex-1">
                <span className="text-xs font-bold text-slate-400">STEP {s.n}</span>
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{s.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{s.body}</p>
                {s.points && (
                  <ul className="mt-3 space-y-1.5">
                    {s.points.map((p, i) => <li key={`${s.n}-${i}`} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {p}</li>)}
                  </ul>
                )}
                {s.action && (
                  <button onClick={() => navigate(s.action.to)} data-testid={`training-goto-${s.n}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-bh-blue hover:text-bh-blue-dark">{s.action.label} <ArrowRight className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "script" && (
        <div className="space-y-4 no-print" data-testid="training-script">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3 text-sm text-blue-900">
            <Play className="w-5 h-5 shrink-0 mt-0.5" />
            <div><b>How to use this:</b> Open the screens under each scene, start a screen recorder, and read the “Say” lines aloud as you click. Timings are a guide.</div>
          </div>
          {topic.scenes.map((sc, i) => (
            <div key={`${sc.t}-${i}`} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`training-scene-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-xs font-mono font-bold bg-slate-900 text-white px-2 py-1 rounded">{sc.t}</span>
                <h4 className="font-bold text-slate-900">{sc.title}</h4>
              </div>
              <p className="text-xs uppercase tracking-wider text-bh-orange font-bold mb-1">On screen</p>
              <p className="text-sm text-slate-600 mb-3">{sc.shot}</p>
              <p className="text-xs uppercase tracking-wider text-bh-blue font-bold mb-1">Say</p>
              <p className="text-sm text-slate-800 leading-relaxed italic">“{sc.say}”</p>
            </div>
          ))}
        </div>
      )}

      {tab === "sop" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 no-print" data-testid="training-sop">
          <SopContent topic={topic} />
        </div>
      )}

      <div id="sop-print" className="hidden">
        <SopContent topic={topic} forPrint />
      </div>
    </div>
  );
}

function SopContent({ topic, forPrint = false }) {
  return (
    <div className={forPrint ? "text-[12px] leading-snug text-black" : ""}>
      <div className="flex items-center justify-between border-b-2 border-bh-orange pb-3 mb-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{topic.title.replace(/^How to /, "").replace(/^\w/, (c) => c.toUpperCase())} — Quick SOP</h2>
          <p className="text-sm text-slate-500">Keep this handy at the workstation.</p>
        </div>
        <Icon name={topic.icon} className="w-9 h-9 text-bh-orange" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        {topic.sop.map((sec) => (
          <div key={sec.h}>
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-bh-blue mb-2">{sec.h}</h3>
            <ul className="space-y-1.5">
              {sec.items.map((it, i) => <li key={`${sec.h}-${i}`} className="flex items-start gap-2 text-sm text-slate-800"><span className="text-bh-orange font-bold mt-0.5">›</span> {it}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
